import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  Brain,
  ChevronDown,
  ChevronRight,
  Layers,
  ListOrdered,
  Map,
  Presentation,
} from 'lucide-react';
import { cn } from '@/lib/utils';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const TABS = [
  { id: 'concepts', label: 'Concepts', icon: Layers },
  { id: 'teaching_plan', label: 'Teaching plan', icon: Presentation },
  { id: 'rankings', label: 'Final report', icon: BarChart3 },
  { id: 'prompt_map', label: 'Prompt map', icon: Map },
];

const PART_COLORS = [
  'bg-violet-500/20 text-violet-200 border-violet-500/30',
  'bg-sky-500/20 text-sky-200 border-sky-500/30',
  'bg-emerald-500/20 text-emerald-200 border-emerald-500/30',
  'bg-amber-500/20 text-amber-200 border-amber-500/30',
  'bg-rose-500/20 text-rose-200 border-rose-500/30',
  'bg-cyan-500/20 text-cyan-200 border-cyan-500/30',
];

function partBadgeClass(part) {
  const idx = typeof part === 'number' ? part % PART_COLORS.length : PART_COLORS.length - 1;
  return PART_COLORS[idx];
}

function FieldWithPart({ label, value, part, partTitle }) {
  if (value == null || value === '' || (Array.isArray(value) && !value.length)) return null;
  const display = Array.isArray(value) ? value.join(', ') : String(value);
  return (
    <div className="text-xs space-y-0.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-slate-400">{label}</span>
        {part != null ? (
          <span
            className={cn('text-[9px] px-1.5 py-0.5 rounded border', partBadgeClass(part))}
            title={partTitle}
          >
            Part {part}
          </span>
        ) : null}
      </div>
      <p className="text-slate-200">{display}</p>
    </div>
  );
}

function TeachingPlanSlideCard({ slide, globalIndex }) {
  const role = slide.slide_role || 'artifact';
  const depth = slide.depth ?? 0;
  const indent = Math.min(depth * 12, 48);

  if (role === 'subtopic_overview') {
    return (
      <div
        className="rounded-lg border border-sky-500/30 bg-sky-950/25 p-3"
        style={{ marginLeft: indent }}
      >
        <div className="flex items-start gap-2">
          <span className="text-[10px] text-sky-400 font-mono shrink-0">#{globalIndex}</span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              <span className="text-[9px] uppercase px-1.5 py-0.5 rounded border border-sky-500/40 text-sky-300">
                Overview
              </span>
              <span className="text-[10px] text-slate-500">{slide.concept_id}</span>
              {slide.hierarchy_role ? (
                <span className="text-[9px] text-slate-600">{slide.hierarchy_role}</span>
              ) : null}
            </div>
            <p className="text-sm font-medium text-sky-100">{slide.heading}</p>
            {slide.parent_slide_id ? (
              <p className="text-[10px] text-slate-500 mt-1">Child of slide {slide.parent_slide_id}</p>
            ) : null}
            {slide.subtopics_listed?.length ? (
              <ul className="mt-2 text-xs text-sky-200/80 list-disc pl-4 space-y-0.5">
                {slide.subtopics_listed.map((sub) => (
                  <li key={sub}>{sub}</li>
                ))}
              </ul>
            ) : null}
            {slide.planned_content_hint ? (
              <p className="text-xs text-slate-400 mt-2">{slide.planned_content_hint}</p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (role === 'metadata_slide') {
    const meta = slide.metadata || {};
    return (
      <div
        className="rounded-lg border border-amber-500/35 bg-amber-950/20 p-3"
        style={{ marginLeft: indent }}
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              <span className="text-[9px] uppercase px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300">
                Coverage gap
              </span>
              <span className="text-[10px] text-slate-500">{slide.concept_id}</span>
            </div>
            <p className="text-sm font-medium text-amber-100">{meta.title || slide.heading}</p>
            <p className="text-xs text-amber-200/80 mt-1">{meta.body || slide.planned_content_hint}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border border-violet-500/25 bg-slate-950/50 p-3"
      style={{ marginLeft: indent }}
    >
      <div className="flex items-start gap-2">
        <span className="text-[10px] text-violet-400 font-mono shrink-0">#{globalIndex}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <span className="text-[9px] uppercase px-1.5 py-0.5 rounded border border-violet-500/35 text-violet-300">
              {slide.artifact_type || 'Artifact'}
            </span>
            <span className="text-[10px] text-slate-500">{slide.concept_name || slide.concept_id}</span>
            {slide.hierarchy_role ? (
              <span className="text-[9px] text-slate-600">{slide.hierarchy_role}</span>
            ) : null}
          </div>
          <p className="text-sm text-violet-100">{slide.heading}</p>
          {slide.parent_slide_id ? (
            <p className="text-[10px] text-slate-500 mt-1">Child of slide {slide.parent_slide_id}</p>
          ) : null}
          {slide.ancestor_artifact_types?.length ? (
            <p className="text-[10px] text-slate-600 mt-1">
              Excludes parent artifacts: {slide.ancestor_artifact_types.join(', ')}
            </p>
          ) : null}
          {slide.planned_content_hint ? (
            <p className="text-xs text-slate-400 mt-1">{slide.planned_content_hint}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function TeachingPlanPanel({ data }) {
  const slides = useMemo(() => {
    const flat = data?.teaching_slides_flat;
    if (Array.isArray(flat) && flat.length) return flat;
    const out = [];
    const concepts = [...(data?.concepts || [])].sort(
      (a, b) => (a.tree_order ?? 999) - (b.tree_order ?? 999),
    );
    concepts.forEach((c) => {
      (c.teaching_slides || []).forEach((s) => {
        out.push({
          ...s,
          concept_id: c.concept_id,
          concept_name: c.name,
          tree_order: c.tree_order,
          depth: c.depth,
        });
      });
    });
    return out;
  }, [data]);

  const sequence = data?.teaching_sequence || [];

  if (!slides.length) {
    return (
      <p className="text-xs text-slate-500 py-4">
        No teaching slide plan yet. Run knowledge analysis to generate per-concept slides.
      </p>
    );
  }

  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
      {sequence.length ? (
        <p className="text-[10px] text-slate-500">
          Tree order: {sequence.join(' → ')}
        </p>
      ) : null}
      {slides.map((slide, idx) => (
        <TeachingPlanSlideCard key={slide.slide_id || `${slide.concept_id}-${idx}`} slide={slide} globalIndex={idx + 1} />
      ))}
    </div>
  );
}

function ConceptAnalysisCard({ concept, sectionIndex }) {
  const [open, setOpen] = useState(false);
  const fieldToPart = useMemo(() => {
    const map = {};
    (sectionIndex || []).forEach((sec) => {
      (sec.fields || []).forEach((f) => {
        if (!map[f]) map[f] = sec;
      });
    });
    return map;
  }, [sectionIndex]);

  const artifactRows = useMemo(() => {
    const list = concept.suggested_artifacts?.length
      ? concept.suggested_artifacts
      : concept.top_artifacts;
    if (Array.isArray(list) && list.length) {
      return list.map((row) => ({
        name: row.name,
        score: row.relevance_score ?? row.score,
        reason: row.reason || concept.artifact_reasons?.[row.name] || '',
      }));
    }
    return [];
  }, [concept]);

  return (
    <div className="rounded-lg border border-slate-700/80 bg-slate-950/50 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-800/40"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? (
          <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <span className="text-sm font-medium text-violet-100">{concept.name}</span>
          <span className="text-[10px] text-slate-500 ml-2">{concept.concept_id}</span>
          <span className="text-[10px] text-slate-600 ml-1">L{concept.depth}</span>
        </div>
        <span className="text-[10px] text-amber-300/80 shrink-0">
          D{concept.difficulty ?? '—'}
        </span>
      </button>
      {open ? (
        <div className="px-3 pb-3 space-y-3 border-t border-slate-800/80 pt-3">
          <div className="flex flex-wrap gap-1">
            {(sectionIndex || []).slice(0, 8).map((sec) => (
              <span
                key={sec.part}
                className={cn('text-[9px] px-1.5 py-0.5 rounded border', partBadgeClass(sec.part))}
                title={(sec.fields || []).join(', ')}
              >
                P{sec.part}: {sec.title}
              </span>
            ))}
          </div>

          <FieldWithPart
            label="Definition"
            value={concept.definition || concept.short_definition}
            part={fieldToPart.definition?.part}
            partTitle={fieldToPart.definition?.title}
          />
          <FieldWithPart
            label="Purpose"
            value={concept.purpose}
            part={fieldToPart.purpose?.part}
            partTitle={fieldToPart.purpose?.title}
          />
          <FieldWithPart
            label="Prerequisites"
            value={concept.prerequisites || concept.direct_prerequisites}
            part={fieldToPart.prerequisites?.part}
            partTitle={fieldToPart.prerequisites?.title}
          />
          <FieldWithPart
            label="Learning stage"
            value={concept.learning_stage}
            part={fieldToPart.learning_stage?.part}
            partTitle={fieldToPart.learning_stage?.title}
          />
          <FieldWithPart
            label="Dependency score"
            value={concept.dependency_score}
            part={fieldToPart.dependency_score?.part}
            partTitle={fieldToPart.dependency_score?.title}
          />

          <div>
            <p className="text-[10px] uppercase text-slate-500 mb-1 flex items-center gap-1">
              <TwinneeLogo className="w-3 h-3" />
              Suggested artifacts (only if needed for clarity)
              <span className={cn('text-[9px] px-1 rounded border ml-1', partBadgeClass(7))}>Part 7</span>
            </p>
            {artifactRows.length > 0 ? (
              <ul className="space-y-2">
                {artifactRows.map((row) => (
                  <li key={row.name} className="text-xs text-slate-300">
                    <div className="flex justify-between gap-2 items-center">
                      <span className="font-medium text-violet-100">{row.name}</span>
                      <span className="text-violet-300 tabular-nums shrink-0">{row.score}/10</span>
                    </div>
                    {row.reason ? (
                      <p className="text-[11px] text-slate-500 mt-0.5">{row.reason}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[11px] text-slate-500 italic">
                None required — narrative explanation is sufficient for this concept.
              </p>
            )}
          </div>

          {concept.slide_estimate ? (
            <FieldWithPart
              label={`Slides (${concept.slide_estimate.minimum}–${concept.slide_estimate.maximum}, rec ${concept.slide_estimate.recommended})`}
              value={concept.slide_estimate.reasoning}
              part={12}
              partTitle="Slide Estimation"
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function RankingsPanel({ report }) {
  if (!report) return <p className="text-sm text-slate-500">No final report yet.</p>;
  const sections = [
    { key: 'concept_ranking_by_importance', label: 'By importance', part: 'final' },
    { key: 'concept_ranking_by_difficulty', label: 'By difficulty', part: 'final' },
    { key: 'highest_dependency_concepts', label: 'Highest dependency', part: 'final' },
    { key: 'highest_cognitive_load_concepts', label: 'Highest cognitive load', part: 'final' },
    { key: 'most_visual_concepts', label: 'Most visual', part: 'final' },
    { key: 'recommended_learning_sequence', label: 'Recommended sequence', part: 'final' },
  ];

  return (
    <div className="space-y-4">
      {report.curriculum_readiness_report ? (
        <div className="rounded-lg border border-emerald-500/25 bg-emerald-950/20 p-3">
          <p className="text-[10px] uppercase text-emerald-400 mb-1 flex items-center gap-1">
            <Brain className="w-3.5 h-3.5" />
            Curriculum readiness
            <span className="text-[9px] px-1 rounded border border-emerald-500/30 ml-1">Final report</span>
          </p>
          <p className="text-sm text-emerald-100/90">{report.curriculum_readiness_report}</p>
        </div>
      ) : null}

      {sections.map(({ key, label }) => {
        const rows = report[key];
        if (!rows?.length) return null;
        return (
          <div key={key}>
            <p className="text-[10px] uppercase text-slate-500 mb-2 flex items-center gap-1">
              <ListOrdered className="w-3.5 h-3.5" />
              {label}
            </p>
            <ul className="space-y-1 text-xs text-slate-300">
              {rows.slice(0, 12).map((row, idx) => {
                const name = typeof row === 'string' ? row : row.name || row.concept;
                const extra = typeof row === 'object' && row.score != null ? ` (${row.score})` : '';
                return (
                  <li key={`${key}-${idx}`} className="flex gap-2">
                    <span className="text-slate-600 w-5">{idx + 1}.</span>
                    <span>{name}{extra}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}

      {report.missing_concepts_report?.length ? (
        <div>
          <p className="text-[10px] uppercase text-amber-400 mb-2">Missing concepts</p>
          <ul className="text-xs text-amber-200/80 list-disc pl-4">
            {report.missing_concepts_report.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function PromptMapPanel({ sectionIndex, steps }) {
  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-400">
        Each prompt part maps to JSON fields consumed by downstream course, slide, and artifact generators.
      </p>

      {(sectionIndex || []).map((sec) => (
        <div
          key={String(sec.part)}
          className="rounded-lg border border-slate-700/60 bg-slate-950/40 p-3"
        >
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={cn('text-[10px] px-2 py-0.5 rounded border font-medium', partBadgeClass(sec.part))}>
              {sec.part === 'final' ? 'Final report' : `Part ${sec.part}`}
            </span>
            <span className="text-sm text-violet-100">{sec.title}</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {(sec.fields || []).map((f) => (
              <code
                key={f}
                className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-emerald-200/90"
              >
                {f}
              </code>
            ))}
          </div>
        </div>
      ))}

      {steps?.length ? (
        <div className="rounded-lg border border-violet-500/20 bg-violet-950/20 p-3">
          <p className="text-[10px] uppercase text-violet-300 mb-2">Pipeline step → prompt coverage</p>
          <ul className="space-y-2 text-xs">
            {steps.map((step) => (
              <li key={step.id}>
                <span className="text-violet-100 font-medium">{step.label}</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(step.promptParts || []).map((p) => (
                    <span key={p} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">
                      {p}
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function KnowledgeAnalysisV3Panel({ data, pipelineSteps, className }) {
  const [tab, setTab] = useState('concepts');
  const concepts = data?.concepts || [];
  const sectionIndex = data?.prompt_section_index || [];
  const slideCount = data?.teaching_slides_flat?.length
    || concepts.reduce((n, c) => n + (c.teaching_slides?.length || 0), 0);

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex flex-wrap gap-1 border-b border-slate-700/60 pb-2">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors',
              tab === id
                ? 'bg-violet-600/30 text-violet-100'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50',
            )}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
            {id === 'concepts' && concepts.length ? (
              <span className="text-[10px] text-slate-500">({concepts.length})</span>
            ) : null}
            {id === 'teaching_plan' && slideCount ? (
              <span className="text-[10px] text-slate-500">({slideCount})</span>
            ) : null}
          </button>
        ))}
      </div>

      {tab === 'concepts' ? (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {concepts.map((c) => (
            <ConceptAnalysisCard key={c.concept_id} concept={c} sectionIndex={sectionIndex} />
          ))}
        </div>
      ) : null}

      {tab === 'teaching_plan' ? <TeachingPlanPanel data={data} /> : null}

      {tab === 'rankings' ? <RankingsPanel report={data?.final_report} /> : null}

      {tab === 'prompt_map' ? (
        <PromptMapPanel sectionIndex={sectionIndex} steps={pipelineSteps} />
      ) : null}
    </div>
  );
}
